export type AnalyticsEventName =
  | 'additional_business_type_clicked'
  | 'business_category_selected'
  | 'business_type_selected'
  | 'category_limit_reached'
  | 'demo_interest_click'
  | 'directory_business_type_filter_applied'
  | 'directory_category_filter_applied'
  | 'faq_open'
  | 'generate_lead'
  | 'professional_benefits_expanded'
  | 'professional_upgrade_clicked'
  | 'professional_upsell_viewed'
  | 'pricing_cta_click'
  | 'pricing_view'
  | 'scroll_depth'
  | 'section_view'
  | 'service_limit_reached'
  | 'whatsapp_cta_click';

export type AnalyticsParamName =
  | 'business_type_slug'
  | 'category_slug'
  | 'classification_count'
  | 'cta_location'
  | 'cta_text'
  | 'device_type'
  | 'faq_question'
  | 'feature_name'
  | 'landing_variant'
  | 'method'
  | 'page_path'
  | 'page_title'
  | 'plan_code'
  | 'pricing_plan'
  | 'scroll_percent'
  | 'section_name'
  | 'service_count'
  | 'source'
  | 'upsell_context';

export type AnalyticsParams = Partial<Record<AnalyticsParamName, string | number>>;

type AnalyticsPayload = Partial<Record<AnalyticsParamName, string | number>>;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (command: 'event', eventName: string, params: Record<string, unknown>) => void;
  }
}

const ALLOWED_PARAM_NAMES: AnalyticsParamName[] = [
  'business_type_slug',
  'category_slug',
  'classification_count',
  'cta_location',
  'cta_text',
  'device_type',
  'faq_question',
  'feature_name',
  'landing_variant',
  'method',
  'page_path',
  'page_title',
  'plan_code',
  'pricing_plan',
  'scroll_percent',
  'section_name',
  'service_count',
  'source',
  'upsell_context',
];

const LANDING_VARIANT = process.env.NEXT_PUBLIC_LANDING_VARIANT?.trim() || 'default';

function getDeviceType(): string {
  if (window.innerWidth < 768) return 'mobile';
  if (window.innerWidth < 1024) return 'tablet';
  return 'desktop';
}

function cleanValue(value: string | number): string | number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  const normalized = value.replace(/\s+/g, ' ').trim();
  return normalized ? normalized.slice(0, 160) : undefined;
}

function buildPayload(params: AnalyticsParams): AnalyticsPayload {
  const source: AnalyticsParams = {
    page_path: window.location.pathname,
    page_title: document.title,
    device_type: getDeviceType(),
    landing_variant: LANDING_VARIANT,
    ...params,
  };
  const payload: AnalyticsPayload = {};

  ALLOWED_PARAM_NAMES.forEach((paramName) => {
    const value = source[paramName];
    if (value === undefined) return;

    const cleanedValue = cleanValue(value);
    if (cleanedValue !== undefined) {
      payload[paramName] = cleanedValue;
    }
  });

  return payload;
}

function isDebugEnabled(): boolean {
  return (
    process.env.NODE_ENV !== 'production' ||
    process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true' ||
    new URLSearchParams(window.location.search).has('analytics_debug')
  );
}

export function trackEvent(eventName: AnalyticsEventName, params: AnalyticsParams = {}): void {
  if (typeof window === 'undefined') return;

  const payload = buildPayload(params);
  let destination = 'none';

  try {
    if (typeof window.gtag === 'function') {
      destination = 'gtag';
      window.gtag('event', eventName, payload);
    } else if (Array.isArray(window.dataLayer)) {
      destination = 'dataLayer';
      window.dataLayer.push({ event: eventName, ...payload });
    }
  } catch (error) {
    destination = 'error';
    if (isDebugEnabled()) {
      console.error('[analytics] No se pudo enviar el evento', error);
    }
  }

  window.dispatchEvent(
    new CustomEvent('oy:analytics', {
      detail: { event: eventName, params: payload, destination },
    })
  );

  if (isDebugEnabled()) {
    console.debug('[analytics]', eventName, payload, `destination=${destination}`);
  }
}
